import type { TableColumn } from "./types";

// Read-side mirror of the backend's `_coerce_table` leniency: task outputs
// carry table values as JSON strings (connectors are string-typed) with
// cells sometimes keyed by column label instead of name. The backend
// normalizes on commit; this normalizes for display so the grid doesn't
// render an empty table for data that committed fine.

function parseCandidate(value: unknown): unknown {
  if (typeof value !== "string") return value;
  let candidate: unknown = value.trim();
  for (let i = 0; i < 2 && typeof candidate === "string"; i++) {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return value;
    }
  }
  return candidate;
}

export function normalizeTableRows(
  value: unknown,
  columns: TableColumn[]
): Record<string, unknown>[] {
  const parsed = parseCandidate(value);
  if (!Array.isArray(parsed)) return [];

  const names = new Set(columns.map((c) => c.name));
  const nameByLabel = new Map<string, string>();
  for (const col of columns) {
    if (!nameByLabel.has(col.label)) nameByLabel.set(col.label, col.name);
  }

  return parsed
    .filter(
      (r): r is Record<string, unknown> => typeof r === "object" && r !== null
    )
    .map((row) => {
      const out: Record<string, unknown> = {};
      for (const [key, cell] of Object.entries(row)) {
        out[names.has(key) ? key : (nameByLabel.get(key) ?? key)] = cell;
      }
      return out;
    });
}
